/* OnSumn v2 — Glass Event Cards + Grid */

/* City display comes from ONE authority — lib/city-registry.js. This is the
   first real ES import in components/*.jsx, and it is deliberate: the
   2026-08-17 Shoreline incident happened because this file kept its OWN city
   ladder (six venue-name guesses ending `else city = 'SF'`) alongside the
   pipeline's, so a scraper-side repair could not reach the screen. A window
   read would have re-created that drift the moment a script tag moved; an
   import makes "Events.jsx has a private notion of city" a build error rather
   than a code-review habit. Vite bundles it into one shared chunk across every
   entry that imports it (see the module header for the full argument). */
import { toDisplayCity, cityToTag, cityInk } from '../lib/city-registry.js';

/* Venue entry-rules rail (ENTRY chip → RulesPopover) — gate, matcher, chip
   tokens and the one-fetch-per-page policies hook all come from ONE shared
   module, consumed by this card AND VenueEventList's row cluster, so the two
   chip surfaces can never disagree about whether a venue has rules. The gate
   itself is a lockstep twin of scrapers/venue-policies.js#isRenderable
   (guarded by tests/venue-rules-gate.test.js). */
import {
  useVenuePolicies,
  policyChipForEvent,
  rulesChipStyle,
  rulesChipSquareStyle,
  rulesChipSquareHidden,
  rulesChipAria,
  rulesChipTitle,
  rulesChipLabel,
  chipKeyDownGuard,
} from '../lib/venue-policies-gate.js';

/* Occasion kicker (seasonal lane C3): gate, roster label, hub link, name
   and both-theme tokens come from ONE module, shared with VenueEventList's
   row cluster (Ruling R13), so the card pill and the venue-row pill can
   never drift. */
import { occasionKicker } from '../lib/occasion-kicker.js';

/* Live events — loaded from events.json by loadLiveEvents(). Starts empty;
   EventsGrid renders SkeletonCard placeholders during the fetch window, and
   EmptyState if the fetch fails. No more hardcoded fallback data — the
   previous FALLBACK_EVENTS array was early-dev demo data without slug /
   image / link, which surfaced as broken cards (no image, non-interactive)
   in any category the scrapers didn't yet cover (most visibly CARDS). */
const ALL_EVENTS = [];
const _eventsLoading = { current: false, done: false, failed: false, listeners: [] };
const _eventsListeners = []; // persistent listeners — fired on any mutation
let _eventsGenerated = null; // ISO timestamp from last scrape

/* Defense-in-depth: read a field that MAY be wrapped as {value, source, set_at}.
   The worker's projectEvent helper already unwraps these before responding, so
   in practice this returns the raw string. But if anything ever slips through
   wrapped (e.g., a future endpoint that forgets to project), we still cope. */
function getField(ev, key) {
  const f = ev?.[key];
  if (f && typeof f === 'object' && 'value' in f && 'source' in f) return f.value;
  return f;
}

/* Mutator: prepend a new event and notify all useEvents() subscribers.
   Used by Phase 4's flyer-upload modal to surface a freshly-extracted event live. */
function addEvent(ev) {
  if (!ev || typeof ev !== 'object') return;
  ALL_EVENTS.unshift(ev);
  _eventsListeners.forEach(fn => { try { fn(); } catch {} });
}

// Last-line-of-defense dedup applied AFTER scrapers/normalize.js#deduplicateEvents
// has already run server-side. KEEP THIS NET: events.json is served from
// Pages cache and can trail a merged server-side fix by up to a full cron
// cycle — during that window this is the only thing between a stale payload
// and a doubled card. 2026-07-29 (Goal B): the net is TITLE-GATED now. The
// old venue|date|time bucket-collapse kept one row per bucket with no title
// check and silently deleted seven genuinely distinct events (three
// different GAMH shows on one night rendered as one card). Collapse
// decisions now come from the SAME duplicatePairVerdict the server merger
// and the run-all guards use (lib/event-identity.js), via the extracted,
// root-tested engine in lib/client-dedup.js — one identity notion across
// every surface. D3 (2026-08-07): the bucket key is injected too —
// venueIsoBucketKey, the MACHINE day (iso_date/startDate), never the display
// date string two scrapers can spell two ways (the MALLORAMA "Sep 5"/"Sep 6"
// class). All identity inputs load as module scripts (window.EventIdentity
// / window.ClientDedup); if ANY is missing we pass rows through UNTOUCHED:
// a missing net shows a visible duplicate, a wrong net deletes a real show.
function clientDedup(events) {
  const engine = typeof window !== 'undefined' ? window.ClientDedup : undefined;
  const identity = typeof window !== 'undefined' ? window.EventIdentity : undefined;
  if (!engine || !identity || typeof engine.dedupeClientEvents !== 'function'
      || typeof identity.duplicatePairVerdict !== 'function'
      || typeof identity.venueIsoBucketKey !== 'function') {
    return Array.isArray(events) ? events.slice() : [];
  }
  return engine.dedupeClientEvents(events, identity.duplicatePairVerdict, identity.venueIsoBucketKey);
}

function loadLiveEvents() {
  if (_eventsLoading.current || _eventsLoading.done) return;
  _eventsLoading.current = true;

  // D3: consume the ONE shared read (lib/event-feed.js) — Events.jsx and
  // use-search-index.js used to fetch events.json separately, so the feed
  // and Spotlight could give two different answers about the same night.
  // window.EventFeed.load() memoizes one fetch + one net for every consumer
  // on the page. Fallback (loader script missing/blocked): fetch directly
  // and net locally — same doctrine, degraded to this page only.
  // P3 (2026-08-26): the fallback shares the ONE raw single-flight read
  // (window.__EVENT_FEED_RAW__ — the same key lib/event-feed.js layers its
  // netted memo on), so a consumer racing ahead of the loader module can
  // never split the page into two events.json requests.
  const hasLoader = typeof window !== 'undefined'
      && window.EventFeed && typeof window.EventFeed.load === 'function';
  if (!hasLoader && typeof window !== 'undefined' && !window.__EVENT_FEED_RAW__) {
    window.__EVENT_FEED_RAW__ = fetch('/events.json')
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });
  }
  const feedPromise = hasLoader
    ? window.EventFeed.load()
    : window.__EVENT_FEED_RAW__
        .then(data => ({
          events: clientDedup((data && data.events) || []),
          generated: (data && data.generated) || null,
        }));

  feedPromise
    .then(({ events, generated }) => {
      if (events && events.length > 0) {
        ALL_EVENTS.length = 0;
        // withCanonicalCity on the way IN, not at each render site: the feed
        // and the worker rows then share one city/tag rule, and no surface
        // downstream has to remember to apply it (which is precisely how the
        // client grew a second city ladder in the first place). Applied AFTER
        // the dedup net — identity never keys on city, so the order is free.
        events.forEach(e => ALL_EVENTS.push(withCanonicalCity(e)));
        _eventsGenerated = generated || null;
      }
    })
    .catch(() => {
      /* events.json fetch failed — silent fall-through to fallback events, but
         flag the failure (B5) so deep-link resolution can tell a TRANSIENT miss
         (source didn't load) from a genuine "gone" slug (source loaded, slug
         absent). Without this, a failed events.json makes every deep-link read
         as N2 "that one's gone." */
      _eventsLoading.failed = true;
    })
    .finally(() => {
      _eventsLoading.current = false;
      _eventsLoading.done = true;
      _eventsLoading.listeners.forEach(fn => fn());
      _eventsLoading.listeners = [];
      // Once core events are loaded, fetch user-submitted ones from the worker.
      loadUserEvents();
    });
}

/* THE one place a client row's city/tag is settled.
 *
 * Applied to EVERY row that reaches the feed — events.json rows on load and
 * worker rows through normalizeWorkerEvent — so a single rule governs both.
 * It DERIVES NOTHING: the city is whatever the upstream authority
 * (scrapers/city-authority.js) established, canonicalized for spelling only,
 * and the tag falls out of that city through the ONE declared map. A row whose
 * city the authority refused arrives here as null and leaves as null.
 *
 * The row's own `tag` is deliberately DISCARDED rather than trusted. Before
 * this repair a stale/wrong tag was free to disagree with its city (events.json
 * carried 2 rows tagged "SF MISSION" — a neighbourhood, not a municipality),
 * and a chip that disagrees with the claim is a second claim. One derivation,
 * one answer. */
function withCanonicalCity(e) {
  const city = toDisplayCity(getField(e, 'city') ?? e.city);
  return { ...e, city, tag: cityToTag(city) };
}

/* Normalize a raw worker event (user-submitted or venue-spine) into the flat
   shape the grid/map expect: defensively unwrap any provenance-wrapped fields
   and settle city/tag through withCanonicalCity.
 *
 * WHAT THIS NO LONGER DOES (2026-08-17):
 *   - It no longer guesses a city from the venue string. The deleted ladder
 *     tested six substrings and ended `else city = 'SF'`, so a Mountain View
 *     amphitheatre, a Napa winery and a Half Moon Bay brewery all became San
 *     Francisco — a positive false claim, generated on the client, immune to
 *     every upstream fix.
 *   - It no longer invents coordinates. The deleted fallback stamped
 *     37.7749/-122.4194 — downtown SF — onto any row missing lat/lng, which
 *     put a pin on the map at a place the event is not. A coordinate-less row
 *     now keeps null coordinates; MapView already skips those (it null-checks
 *     before creating a marker and again before fitting bounds), and
 *     isValidEvent below holds them out of the feed entirely. Either way the
 *     map never shows a pin we made up. */
function normalizeWorkerEvent(ue) {
  return withCanonicalCity({
    ...ue,
    id: ue.id || ue.slug || ue.event_hash,
    title:    getField(ue, 'title')    ?? ue.title,
    date:     getField(ue, 'date')     ?? ue.date,
    time:     getField(ue, 'time')     ?? ue.time,
    venue:    getField(ue, 'venue')    ?? ue.venue,
    address:  getField(ue, 'address')  ?? ue.address,
    category: getField(ue, 'category') ?? ue.category,
    desc:     (getField(ue, 'desc') ?? ue.desc) || ue.description || '',
    lat: typeof ue.lat === 'number' ? ue.lat : null,
    lng: typeof ue.lng === 'number' ? ue.lng : null,
    provenance: ue.provenance || 'user',
    _provenance: ue._provenance || {},
  });
}

/* Venue slug lookup — maps canonical names AND common aliases to the
   /venue/[slug] route. Source of truth is components/VenuePage.jsx
   VENUE_REGISTRY; we duplicate canonicals here (Events.jsx loads BEFORE
   VenuePage.jsx) and add Vox-curated aliases for SEO funneling.

   findVenueSlug(name) → slug | null. Returns null when no entry matches
   — caller renders plain text in that case. Vox's call: the win is
   500+ event cards → 7 hub pages. Don't link to 404s. */
const _VENUE_SLUG_LOOKUP = (() => {
  const entries = [
    { slug: 'the-fillmore', canonical: 'The Fillmore', aliases: ['Fillmore', 'Fillmore SF', 'The Fillmore SF', 'Fillmore Auditorium'] },
    { slug: 'fox-theater-oakland', canonical: 'Fox Theater Oakland', aliases: ['Fox Theater', 'Fox Oakland', 'The Fox Theater', 'The Fox Oakland', 'Fox Theatre Oakland'] },
    { slug: 'oakland-arena', canonical: 'Oakland Arena', aliases: ['Oakland-Alameda County Coliseum Arena', 'Oracle Arena', 'Oakland Coliseum Arena'] },
    { slug: 'frost-amphitheater', canonical: 'Frost Amphitheater', aliases: ['Frost', 'Frost Amphitheatre', 'Stanford Frost', 'Frost Amphitheater Stanford'] },
    { slug: 'sweetwater-music-hall', canonical: 'Sweetwater Music Hall', aliases: ['Sweetwater', 'The Sweetwater', 'Sweetwater Mill Valley'] },
    { slug: 'the-catalyst', canonical: 'The Catalyst', aliases: ['Catalyst', 'Catalyst Santa Cruz', 'The Catalyst Santa Cruz', 'Catalyst Club'] },
    { slug: 'bill-graham-civic-auditorium', canonical: 'Bill Graham Civic Auditorium', aliases: ['Bill Graham Civic', 'BGCA', 'Bill Graham', 'Bill Graham Auditorium', 'Civic Auditorium'] },
  ];
  const map = new Map();
  const norm = (s) => String(s || '').trim().toLowerCase();
  for (const e of entries) {
    map.set(norm(e.canonical), e.slug);
    for (const a of (e.aliases || [])) map.set(norm(a), e.slug);
  }
  return map;
})();

function findVenueSlug(venueName) {
  if (!venueName) return null;
  const key = String(venueName).trim().toLowerCase();
  return _VENUE_SLUG_LOOKUP.get(key) || null;
}

// Expose for VenuePage/Map reuse without re-declaring the table.
if (typeof window !== 'undefined') window.findVenueSlug = findVenueSlug;

/* Normalize a date string to its first concrete date so re-uploads with
   slightly different extractions ("Jun 6" vs "Jun 6 & 9") collapse together.
   MIRROR of worker/dateNormalize.js#normalizeDate — keep in sync when editing. */
function _normalizeDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  let s = dateStr.trim().toLowerCase();
  if (!s) return '';
  const cut = s.search(/[&,\-–—\/]/);
  if (cut >= 0) s = s.slice(0, cut);
  s = s.replace(/\s+/g, ' ').trim();
  const m = s.match(/^([a-z]{3})\s+0*(\d{1,2})$/);
  if (m) return `${m[1]} ${m[2]}`;
  return s;
}

/* Dedup composite key for merging USER_EVENTS against VENUE_EVENTS.
   place_id + normalized title + normalized date. iso_date wins when present
   (venue spine always sets it); otherwise we normalize the human date string
   so multi-night extractions don't desync ("Jun 6" vs "Jun 6 & 9"). */
function _dedupKey(e) {
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const title = norm(getField(e, 'title') ?? e.title);
  const rawDate = getField(e, 'date') ?? e.date ?? '';
  const day = e.iso_date
    ? String(e.iso_date).slice(0, 10)
    : _normalizeDate(rawDate);
  return `${e.place_id || ''}|${title}|${day}`;
}

/* Merge user-events with venue-events. VENUE_EVENTS wins ties. Returns the
   ordered list to push into ALL_EVENTS: venue events first, then non-colliding
   user events. (Static events.json entries don't carry place_id today, so they
   participate in slug-based dedup via addEvent() rather than this composite key.) */
function dedupEvents(userEvents, venueEvents) {
  const venueKeys = new Set((venueEvents || []).map(_dedupKey));
  const userFiltered = (userEvents || []).filter(e => !venueKeys.has(_dedupKey(e)));
  return [...(venueEvents || []), ...userFiltered];
}

/* Phase 4 + Bay Venue Spine: pull live user-submitted AND venue-spine events
   from the worker in parallel and merge via addEvent, with composite-key dedup
   so a venue-spine record wins against a duplicate user submission. */
const _userEventsLoaded = { current: false, done: false };
async function loadUserEvents() {
  if (_userEventsLoaded.current || _userEventsLoaded.done) return;
  _userEventsLoaded.current = true;
  try {
    // Parallel fetch — Promise.allSettled so one failure doesn't kill the other.
    const [userRes, venueRes] = await Promise.allSettled([
      fetch('/api/events/user'),
      fetch('/api/venue-events'),
    ]);

    let userEvents = [];
    if (userRes.status === 'fulfilled' && userRes.value.ok) {
      const j = await userRes.value.json();
      if (Array.isArray(j)) userEvents = j;
    }

    let venueEvents = [];
    if (venueRes.status === 'fulfilled' && venueRes.value.ok) {
      const j = await venueRes.value.json();
      if (j && Array.isArray(j.events)) venueEvents = j.events;
    }

    const merged = dedupEvents(userEvents, venueEvents);
    const existingSlugs = new Set(ALL_EVENTS.map(e => e.slug).filter(Boolean));
    // Cross-source EXACT-match prefilter against static events.json.
    // VENUE_EVENTS records carry `id`/`event_hash` but no `slug`, so the slug
    // check above misses every duplicate already in events.json via the
    // venue-spine bridge (e.g. "Alex Isley - When The City Sleeps" @ The
    // Fillmore was surfacing twice — once from events.json with poster +
    // 8 PM, once from VENUE_EVENTS with no poster + no time).
    //
    // D3 (2026-08-07): this is a PREFILTER, not an identity notion — the
    // shared authority (venueIsoBucketKey + duplicatePairVerdict) gets the
    // last word in the re-net below. The prefilter cannot be replaced by
    // that key alone: VENUE_EVENTS rows LACK a `time` field, so the
    // time-carrying bucket key puts "poster + 8 PM" and "no poster + no
    // time" in different buckets and never compares them. Exact
    // normalized-title match on the human day is the only net that catches
    // that cross-source class today; it keys the HUMAN date because both
    // sides reliably carry `e.date` while the prefilter predates iso
    // coverage. It can only PREVENT an append (keeping the richer
    // events.json row) — it can never crown a rival survivor, because
    // everything that does append passes the shared verdict next.
    const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const xkey = (e) => {
      const title = norm(getField(e, 'title') ?? e.title);
      const venue = norm(getField(e, 'venue') ?? e.venue);
      const rawDate = getField(e, 'date') ?? e.date ?? '';
      return `${title}|${venue}|${_normalizeDate(rawDate)}`;
    };
    const existingComposite = new Set(ALL_EVENTS.map(xkey));
    let appended = 0;
    merged.forEach(ue => {
      if (ue.slug && existingSlugs.has(ue.slug)) return;
      if (existingComposite.has(xkey(ue))) return;
      const normalized = normalizeWorkerEvent(ue);
      addEvent(normalized);
      appended++;
      if (normalized.slug) existingSlugs.add(normalized.slug);
      existingComposite.add(xkey(normalized));
    });
    // D3: route the combined list through the ONE shared net after append.
    // Worker rows the exact-match prefilter missed (title variants) collapse
    // here under the same verdict + bucket key every other surface uses —
    // no sixth identity notion survives this point.
    if (appended > 0) {
      const renetted = clientDedup(ALL_EVENTS);
      if (renetted.length !== ALL_EVENTS.length) {
        ALL_EVENTS.length = 0;
        renetted.forEach(e => ALL_EVENTS.push(e));
        _eventsListeners.forEach(fn => { try { fn(); } catch {} });
      }
    }
  } catch (e) {
    /* Worker offline / local dev without proxy — silent fail. */
  } finally {
    _userEventsLoaded.current = false;
    _userEventsLoaded.done = true;
  }
}

/* Smooth-scroll + ring-pulse highlight on a card by slug. */
function scrollToEvent(slug) {
  if (!slug) return;
  const tries = [0, 200, 500];
  tries.forEach(delay => {
    setTimeout(() => {
      const target = document.querySelector(`[data-event-slug="${CSS.escape(slug)}"]`);
      if (target && !target.dataset._pulsed) {
        target.dataset._pulsed = '1';
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        if (reduce) {
          target.style.outline = '2px solid var(--international-orange)';
          target.style.outlineOffset = '4px';
          setTimeout(() => { target.style.outline = 'none'; delete target.dataset._pulsed; }, 2000);
        } else {
          target.classList.add('flyer-card-pulse');
          setTimeout(() => { target.classList.remove('flyer-card-pulse'); delete target.dataset._pulsed; }, 1700);
        }
      }
    }, delay);
  });
}

/* Sanitize event URLs — reject anything that isn't http/https */
function sanitizeEventUrl(url) {
  if (!url || typeof url !== 'string') return '';
  return /^https?:\/\//i.test(url) ? url : '';
}

/* safeHref — mirrors Map.jsx scheme guard. Returns the URL only when it is
   a string starting with http:// or https://. Anything else (javascript:,
   data:, vbscript:, null, empty, non-string) returns null so callers can
   gate window.open / href rendering. */
function safeHref(u) {
  return (typeof u === 'string' && /^https?:\/\//i.test(u)) ? u : null;
}

/* Filter out events missing required fields.
 *
 * NOTE the lat/lng legs, which became load-bearing on 2026-08-17: with the
 * invented SF centroid gone from normalizeWorkerEvent, a coordinate-less row
 * fails here and never reaches any surface. That is the conservative reading
 * of "no invented coordinates" — it guarantees no fabricated pin, at the cost
 * of also hiding the card. 68 of 1675 events.json rows are already in that
 * bucket and have been invisible for as long as this gate has existed, so this
 * change moves nobody; it just stops the worker path from smuggling rows past
 * the gate with a made-up point. Letting coordinate-less events into the GRID
 * while keeping them off the map is a real product question (the map already
 * null-checks every consumption site) — deliberately not decided here.
 *
 * `city` is NOT required. A row the city authority refused is a complete,
 * showable event that we simply cannot label — refusing to name a city is not
 * refusing to name the show. */
function isValidEvent(e) {
  if (e.link) e.link = sanitizeEventUrl(e.link);
  if (e.url) e.url = sanitizeEventUrl(e.url);
  return e.lat != null && e.lng != null && e.title && e.date && e.venue;
}

/* Hook to get events with loading state */
function useEvents() {
  const [events, setEvents] = React.useState(ALL_EVENTS.filter(isValidEvent));
  const [loading, setLoading] = React.useState(!_eventsLoading.done);

  React.useEffect(() => {
    const refresh = () => {
      setEvents(ALL_EVENTS.filter(isValidEvent));
      setLoading(false);
    };
    /* Persistent listener — fires on addEvent() and other live mutations */
    _eventsListeners.push(refresh);

    let loadListener = null;
    if (_eventsLoading.done) {
      refresh();
    } else {
      loadLiveEvents();
      loadListener = refresh;
      _eventsLoading.listeners.push(loadListener);
    }

    return () => {
      const i = _eventsListeners.indexOf(refresh);
      if (i >= 0) _eventsListeners.splice(i, 1);
      if (loadListener) {
        const j = _eventsLoading.listeners.indexOf(loadListener);
        if (j >= 0) _eventsLoading.listeners.splice(j, 1);
      }
    };
  }, []);

  // `failed` reflects the events.json fetch specifically (B5) — read from module
  // state at render; the load-completion re-render (setLoading(false)) picks it
  // up, since the .catch sets it before the .finally fires the listeners.
  return { events, loading, generated: _eventsGenerated, failed: _eventsLoading.failed };
}

/* ── Venue Heat (Bay Venue Spine) ──
   Cached at module scope so multiple <EventCard>s share one fetch and a
   <CategoryCarouselRow> rendered from its call site (Map.jsx
   EventsMapSection) automatically sees the same heat data.
   Components consume via useVenueHeat() — no prop drilling required. */
let _venueHeat = {};
let _venueHeatFetched = false;
let _venueHeatInFlight = false;
const _venueHeatListeners = [];

function loadVenueHeat() {
  if (_venueHeatFetched || _venueHeatInFlight) return;
  _venueHeatInFlight = true;
  fetch('/api/venue-heat')
    .then(r => r.ok ? r.json() : { heat: {} })
    .then(j => { _venueHeat = j.heat || {}; })
    .catch(() => { /* silent — pills just don't render */ })
    .finally(() => {
      _venueHeatInFlight = false;
      _venueHeatFetched = true;
      _venueHeatListeners.forEach(fn => { try { fn(); } catch {} });
    });
}

function useVenueHeat() {
  const [heat, setHeat] = React.useState(_venueHeat);
  React.useEffect(() => {
    const listener = () => setHeat(_venueHeat);
    _venueHeatListeners.push(listener);
    if (!_venueHeatFetched && !_venueHeatInFlight) loadVenueHeat();
    else if (_venueHeatFetched) setHeat(_venueHeat);
    return () => {
      const i = _venueHeatListeners.indexOf(listener);
      if (i >= 0) _venueHeatListeners.splice(i, 1);
    };
  }, []);
  return heat;
}

// Interest primitives (markInterested, postInterest, fetchInterestCounts,
// useInterestCount, _interestCache, _interestListeners) live in Interest.jsx
// — shared so venue.html surfaces can fire the ritual too. Load order in
// index.html guarantees Interest.jsx runs before this file.

// FlameButton + ShareButton removed 2026-05-26 (sliding-ritual completion).
// Replaced by SlidingChip + ShareIconButton in components/Interest.jsx.
// `git log -- components/Events.jsx` for the deleted source.

/* ── PUT-ON badge (Phase 4: user-submitted provenance marker) ── */
function PutOnBadge({ event, darkMode }) {
  const handle = event.handle ? `@${event.handle}` : null;
  const tip = handle ? `Added by ${handle} via flyer` : 'Added via flyer drop';
  return (
    <div
      role="img"
      aria-label="Community-submitted event"
      title={tip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        height: 22, padding: '0 8px 0 6px',
        borderRadius: 6,
        background: darkMode ? '#395542' : '#E3EDE6',
        border: darkMode ? '1px solid #5A7A68' : '1px solid #395542',
        color: darkMode ? '#DDEEE3' : '#395542',
        boxShadow: darkMode ? '0 2px 6px rgba(57,85,66,0.30)' : '0 1px 3px rgba(57,85,66,0.18)',
        fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 800,
        letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1,
      }}
    >
      <svg width="11" height="11" viewBox="0 0 48 48" fill="currentColor" aria-hidden="true">
        <path d="M24 4 L26 22 L44 24 L26 26 L24 44 L22 26 L4 24 L22 22 Z" />
      </svg>
      <span>PUT-ON</span>
    </div>
  );
}

/* ── Report button with 5-second undo-toast (Phase 4) ── */
function ReportButton({ slug, darkMode }) {
  const [pending, setPending] = React.useState(false);
  const [reported, setReported] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const fire = async () => {
    try {
      const res = await fetch(`/api/flag/${encodeURIComponent(slug)}`, { method: 'POST' });
      if (res.ok || res.status === 429) setReported(true);
    } catch { /* silent — user already saw confirmation toast */ }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (reported) return;
    if (pending) {
      // Second click within 5s window → cancel
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      setPending(false);
      return;
    }
    setPending(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setPending(false);
      fire();
    }, 5000);
  };

  const iconColor = reported
    ? 'var(--international-orange)'
    : pending
      ? 'var(--international-orange)'
      : (darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.4)');

  return (
    <React.Fragment>
      <button
        type="button"
        className="focus-double-ring-thick"
        onClick={handleClick}
        aria-label={reported ? 'Reported' : (pending ? 'Tap again to cancel report' : 'Report this event')}
        aria-pressed={reported}
        title={reported ? 'Reported' : 'Report'}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          // 44×44 hit area; SVG icon stays 16px.
          width: 44, height: 44,
          borderRadius: '50%',
          background: 'transparent',
          border: 'none',
          cursor: reported ? 'default' : 'pointer',
          opacity: pending || reported ? 1 : 0.55,
          transition: 'opacity 200ms ease',
        }}
        onMouseEnter={e => { if (!reported && !pending) e.currentTarget.style.opacity = 1; }}
        onMouseLeave={e => { if (!reported && !pending) e.currentTarget.style.opacity = 0.55; }}
        onFocus={e => { if (!reported && !pending) e.currentTarget.style.opacity = 1; }}
        onBlur={e => { if (!reported && !pending) e.currentTarget.style.opacity = 0.55; }}
      >
        {reported || pending ? (
          // Flag icon
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 1 L3 15 M3 2 L11 2 L9.5 5 L11 8 L3 8" stroke={iconColor}
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        ) : (
          // Horizontal dots
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="3" cy="8" r="1.4" fill={iconColor} />
            <circle cx="8" cy="8" r="1.4" fill={iconColor} />
            <circle cx="13" cy="8" r="1.4" fill={iconColor} />
          </svg>
        )}
      </button>
      {/* Undo toast (rendered via portal-ish; uses position: fixed) */}
      {pending && (
        <div
          onClick={e => e.stopPropagation()}
          role="status" aria-live="polite"
          style={{
            position: 'fixed', left: '50%',
            // Lift the toast above iOS home-indicator on gesture phones.
            bottom: 'max(32px, env(safe-area-inset-bottom))',
            transform: 'translateX(-50%)',
            zIndex: 9995,
            ...glassStyle(darkMode, 'panel'),
            background: darkMode
              ? 'linear-gradient(145deg, rgba(18,20,20,0.97), rgba(10,10,10,0.98))'
              : 'linear-gradient(145deg, rgba(255,255,255,0.97), rgba(248,248,248,0.98))',
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
            borderRadius: 14,
            padding: '14px 18px',
            display: 'flex', alignItems: 'center', gap: 16,
            fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
            color: darkMode ? '#fff' : '#0A0A0A',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            minWidth: 280,
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <span>reported — thanks for looking out.</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleClick(e); }}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--international-orange-ink)',
              padding: '6px 10px',
              borderRadius: 8,
            }}
          >Undo</button>
        </div>
      )}
      {reported && (
        <span style={{
          position: 'absolute', width: 1, height: 1, overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}>got it. we'll take a look.</span>
      )}
    </React.Fragment>
  );
}

/* The TAG_COLORS table that stood here is gone (2026-08-17). It held 7 hues
   and every miss fell through to international orange — SF's exact hex — so
   under the city authority's ~26-city vocabulary the colour channel would have
   painted Mountain View, Berkeley and Palo Alto in SF's colour while the text
   beside them correctly said otherwise. Ink now comes from lib/city-registry
   #cityInk, which colours the two DECLARED anchors (SF, OAKLAND) and renders
   every other city in a neutral that clears 4.5:1 in both modes. The full
   reasoning, and the measured contrast for each value, lives in that file. */

/* ── Von Restorff hero selector (isolation effect) ──
   Returns true for the ONE card in the currently-rendered, chrono-sorted
   list that gets the Liquid Glass hero treatment. Current rule: the first
   card — under the chrono-sort default that's "the next thing happening
   in the Bay," honest and needing no new data. Named + swappable so the
   rule can later become "editorial pick" without re-plumbing call sites.
   A hero is only designated when the field is big enough to stand out
   from (>= HERO_MIN_FIELD rendered cards); below that, no hero — every
   card stays uniform. One signal, not many: the homepage feed
   (EventsMapSection → CategoryCarouselRow, Map.jsx) consumes this
   FEED-SCOPED — EventsMapSection calls it once against the whole filtered
   feed and heroes only the first card of the first rendered row, so the
   entire grid carries exactly one hero. One-hero-per-row would mint many
   heroes and defeat the rarity. Window-attached below for that cross-
   module call site — Map.jsx is the sole consumer. */
const HERO_MIN_FIELD = 6;
function isHeroCard(event, index, renderedList) {
  if (!Array.isArray(renderedList) || renderedList.length < HERO_MIN_FIELD) return false;
  return index === 0;
}

function EventCard({ event, darkMode, index, radius, taste, venueHeat, onCategoryClick, emphasis, hideOccasionKicker }) {
  // Chip color comes from CATEGORY, not city. Pre-fix the chip background
  // was tagColor (city tag) so a MUSIC event in Oakland rendered amber, in
  // SF orange, in San Jose deep eucalyptus — the chip text said MUSIC but
  // the color meant "where," which read as palette drift. Sourced from
  // lib/category-colors.js (window.CATEGORY_COLORS) with inline fallback.
  //
  // 2026-06-04: NIGHTLIFE moved off the blue family (was #7C5BBC drifting
  // toward #5B7A8D in earlier passes — too close to CARDS' fog blue).
  // Inline fallback kept in lockstep with lib/category-colors.js.
  const CAT_COLORS = (typeof window !== 'undefined' && window.CATEGORY_COLORS) || {
    'MUSIC': '#FF4F00', 'ART': '#5A7A68', 'FOOD': '#D57800',
    'NIGHTLIFE': '#9F7BE5', 'CARDS': '#7A99AC', 'COMMUNITY': '#395542',
  };
  const catColor = CAT_COLORS[event.category] || 'var(--international-orange)';
  const [hov, setHov] = React.useState(false);
  const [cardPulse, setCardPulse] = React.useState(false);
  // Broken-image fallback flips this to true; the gradient+letter branch reads it.
  const [imgError, setImgError] = React.useState(false);
  // MerchPopover trigger state: holds the chip's bounding rect at click
  // time so the popover can FLIP-morph out of it. Null when closed.
  const [merchTriggerRect, setMerchTriggerRect] = React.useState(null);
  // RulesPopover trigger state — same FLIP-rect pattern as merch.
  const [rulesTriggerRect, setRulesTriggerRect] = React.useState(null);
  // Venue policies lookup (null until /data/venue-policies.json resolves; a
  // failed fetch stays null → zero chips, card byte-identical to today).
  // rulesPolicy = {entry, state, eventRules} for a matched, renderable
  // venue OR a row whose own cited `rules` pass the same gate; null = no
  // chip (absent, not disabled — suppressed/stale-suppressed venues too).
  const venuePolicies = useVenuePolicies();
  const rulesPolicy = policyChipForEvent(venuePolicies, event);
  // Occasion kicker: { label, props } for a rostered `event.occasion`, else
  // null (no pill, and the footer stays byte-identical to pre-lane).
  // R24 (Marco, 2026-10-01; Zora §2): inside its OWN occasion's rail the
  // pill is dropped (twelve HALLOWEEN pills under a HALLOWEEN header is
  // wallpaper), so those cards lay out like any untagged card. The prop is
  // set only by an occasion rail (EventsGrid.jsx); everywhere else it is
  // absent and the pill renders as Task 9 shipped it.
  const occasionPill = hideOccasionKicker && event && hideOccasionKicker === event.occasion
    ? null
    : occasionKicker(event, darkMode);
  const pulseTimerRef = React.useRef(null);
  // First 4 cards above the fold get eager-load + fetchPriority="high" so the
  // LCP image is the first card's flyer, not the H1.
  const isAboveFold = typeof index === 'number' && index < 4;

  // Prefer prop (when passed from EventsGrid/CategoryCarouselRow), otherwise
  // fall back to the shared hook — covers call sites that haven't been
  // threaded yet (e.g., Map.jsx's CategoryCarouselRow invocations).
  const heatFromHook = useVenueHeat();
  const heat = venueHeat || heatFromHook;
  // Capitalized alias so JSX treats this as a component, not a DOM tag.
  const VenueHeatPill = window.VenueHeatPill;

  // Clear any pending pulse timer if the card unmounts mid-ritual (e.g.
  // filter chip change within the 250ms window) — avoids setState-on-
  // unmounted. window.open runs synchronously now, so no open-timer to clear.
  React.useEffect(() => () => {
    clearTimeout(pulseTimerRef.current);
  }, []);

  // Dual-mode by construction: cityInk takes darkMode, because one flat brand
  // hex cannot clear AA in both themes (the luminance windows are disjoint).
  const tagColor = cityInk(event.tag, darkMode);
  const bgHues = [25, 15, 35, 200, 30, 20, 160, 40, 28, 190, 10, 45];
  const hue = bgHues[index % bgHues.length];
  const forYou = taste && !taste.skipped && typeof window.tasteBoost === 'function' && window.tasteBoost(event, taste) > 0;

  const handleCardClick = () => {
    if (cardPulse) return; // rate-limit: swallow taps within the 250ms pulse window

    // Prefer an external ticket link when the source carries one (Eventbrite,
    // venue site, etc.). User-submitted events from the flyer pipeline have
    // no event.link/event.url — fall back to the OnSumn /e/[slug] preview so
    // every card has a destination instead of silently no-op'ing. safeHref
    // still gates hostile schemes (javascript:, data:, etc.) on the external
    // path; the internal /e/[slug] is constructed from a server-issued slug.
    const externalUrl = safeHref(event.link || event.url);
    const fallbackUrl = event.slug ? `/e/${encodeURIComponent(event.slug)}` : null;
    const url = externalUrl || fallbackUrl;
    const isFirstMark = markInterested(event.slug);

    // Pulse fires on EVERY tap — visual ack that the gesture registered,
    // regardless of whether it was the first interest mark or a repeat.
    setCardPulse(true);
    pulseTimerRef.current = setTimeout(() => setCardPulse(false), 250);

    if (url) {
      // window.open MUST run synchronously inside the click's user-gesture
      // frame. iOS Safari (and Chrome on iOS) discard the user-activation
      // token after ~50ms, so any setTimeout-deferred window.open is silently
      // popup-blocked — the pulse plays but no tab opens. This bit calendar-
      // panel taps where first-mark events appeared inert: jace 2026-06-04
      // reproduced cleanly. The pulse is pure setState and renders in parallel,
      // so we don't need to defer the nav to let the pulse "land first" —
      // browsers paint the pulse frame and open the new tab in the same loop.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  /* ── Von Restorff emphasis (homepage grid only) ──
     `emphasis` is undefined on every other call site (CategoryCarouselRow
     etc.), which renders the legacy glass card unchanged.
       'hero'  → the ONE lifted, lit card: the 'hero' glass variant
                 (GlassTokens) — brighter tint, large soft elevation
                 shadow, hard 2px ink frame. The distinguishing property
                 is DEPTH, deliberately not orange: the page already
                 spends orange on the logo, DROP A FLYER, chips, and link
                 accents, so an orange ring was never rare — elevation is.
                 (v1 of this treatment used an orange ring; it diluted at
                 full-page scale for exactly that reason.)
       'quiet' → the uniform field: the 'quiet' glass variant — same
                 material as 'card' (blur + layered tint intact) with the
                 inner glow cut to ~half, and NO elevation, so the plane
                 the hero lifts off is genuinely flat. Fully-flat 'solid'
                 was the first cut; across a whole homepage of non-hero
                 cards it read lifeless, so the field dims rather than
                 dies. Quiet flyer imagery is also eased to saturate(0.9)
                 — img leaf only, so category chips / badges keep full
                 palette color — making the hero's artwork the most vivid
                 on the page.
     Distinction is glow/border/ring/shadow ONLY — same dimensions, same
     layout, same 1px border width (the ink frame lives in box-shadow,
     zero layout cost). Static treatment, no motion — nothing to gate on
     reduced-motion. data-emphasis on the <article> is an inert QA marker. */
  const surface = (emphasis === 'hero' || emphasis === 'hero-live')
    ? glassStyle(darkMode, 'hero')
    : emphasis === 'quiet'
      ? glassStyle(darkMode, 'quiet')
      : glassStyle(darkMode, 'card');

  return (
    <article
      data-event-slug={event.slug || ''}
      data-emphasis={emphasis || undefined}
      aria-label={event.title}
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        cursor: 'pointer',
        ...surface,
        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
        borderRadius: radius,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        transform: `translateY(${hov ? -4 : 0}px) scale(${cardPulse ? 1.02 : 1})`,
        boxShadow: hov
          ? (surface.boxShadow + `, 0 16px 40px rgba(${darkMode ? '0,0,0,0.4' : '0,0,0,0.1'})`)
          : surface.boxShadow,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Image area — aspectRatio reserves the box so layout doesn't shift when
       * the flyer loads on slow mobile. Falls back to fixed height if browser
       * doesn't support aspect-ratio yet. */}
      <div style={{
        aspectRatio: '4 / 3',
        minHeight: 120,
        position: 'relative', overflow: 'hidden',
        background: `oklch(${darkMode ? 0.35 : 0.55} 0.05 ${hue})`,
        borderRadius: `${radius}px ${radius}px 0 0`,
      }}>
        {/* Event image or fallback.
         *
         * CF Image Resizing (Pages Pro, wired 2026-05-27 — see
         * project_lighthouse_perf_debt_2026_05_27.md). We wrap with
         * /cdn-cgi/image/<opts>/<src> at render time so flyer URLs from
         * the scrapers (DoTheBay hot-link, Ticketmaster) get
         * AVIF/WebP downsampled to the actual card width. `sizes` matches
         * the grid: full-bleed minus 16px gutters on mobile, ~50vw at
         * tablet, ~33vw at desktop. Defensive 2-step onError chain:
         *   resize 4xx/5xx → swap to raw event.image URL
         *   raw 4xx/5xx    → swap to the gradient+letter placeholder
         * — so a broken CF resize never bricks an LCP card. */}
        {event.image && !imgError ? (
          <img
            src={typeof window !== 'undefined' && typeof window.cfImage === 'function'
              ? window.cfImage(event.image, { width: isAboveFold ? 1200 : 800 })
              : event.image}
            srcSet={typeof window !== 'undefined' && typeof window.cfImageSrcSet === 'function'
              ? window.cfImageSrcSet(event.image, [400, 800, 1200])
              : undefined}
            sizes="(min-width: 1024px) 33vw, (min-width: 600px) 50vw, calc(100vw - 32px)"
            alt={event.title}
            loading={isAboveFold ? 'eager' : 'lazy'}
            fetchPriority={isAboveFold ? 'high' : 'auto'}
            decoding="async"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              // Quiet-field vividness ease (Von Restorff lever 3): the
              // hero's flyer stays 100% saturation while the field's art
              // eases to 90%, so the one lifted card also carries the most
              // vivid imagery. Applied to the <img> LEAF only — never the
              // card or media wrapper — so category chips / date badges
              // keep full palette color and no `filter` containing-block
              // is created above the fixed-position report toast. Static,
              // no transition — nothing to gate on reduced-motion.
              ...(emphasis === 'quiet' ? { filter: 'saturate(0.9)' } : null),
            }}
            // 2-step fallback chain. Marker attr prevents infinite reload
            // loops if the raw URL is also broken (browsers re-fire onError
            // when src changes).
            onError={(e) => {
              const el = e.currentTarget;
              if (el.dataset.cfFallback === 'raw') {
                setImgError(true);
                return;
              }
              const raw = event.image;
              if (raw && el.src !== raw) {
                el.dataset.cfFallback = 'raw';
                el.removeAttribute('srcset');
                el.removeAttribute('sizes');
                el.src = raw;
                return;
              }
              setImgError(true);
            }}
          />
        ) : (() => {
          // Side-stripe replacement spec bundle (Zora 2026-05-26):
          // The 135° diagonal `linear-gradient` overlays here were
          // family-of-violation with the venue side-stripes — both are
          // "AI-aesthetic" surface treatments. Replaced with a flat
          // theme-aware OKLCH-tinted surface (`.cat-surface--*`) plus
          // a brutalist "NO POSTER YET" mono kicker so the placeholder
          // reads as zine-intent, not as broken art direction.
          const catClasses = {
            'MUSIC': 'cat-surface--music',
            'CARDS': 'cat-surface--cards',
            'ART': 'cat-surface--art',
            'FOOD': 'cat-surface--food',
            'NIGHTLIFE': 'cat-surface--nightlife',
            'COMMUNITY': 'cat-surface--community',
          };
          // No-poster watermark color. Pulled from the central palette so
          // the placeholder accent always matches the category chip on the
          // same card. Inline fallback kept in lockstep with
          // lib/category-colors.js (NIGHTLIFE = violet, not steel blue).
          const catAccents = (typeof window !== 'undefined' && window.CATEGORY_COLORS) || {
            'MUSIC': '#FF4F00',
            'CARDS': '#7A99AC',
            'ART': '#5A7A68',
            'FOOD': '#D57800',
            'NIGHTLIFE': '#9F7BE5',
            'COMMUNITY': '#395542',
          };
          const surfaceClass = catClasses[event.category] || catClasses['COMMUNITY'];
          const accent = catAccents[event.category] || catAccents['COMMUNITY'];
          return (
            <div
              className={surfaceClass}
              style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                padding: '12px 12px 10px',
              }}
            >
              {/* Rhea: "NO POSTER YET" mono kicker — owns the empty state
                  with brand voice instead of letting a gradient pretend
                  to be artwork. Kicker sits in the top-left so the title
                  band at the bottom is untouched. */}
              <div
                className="no-poster-kicker"
                style={{
                  position: 'absolute', top: 10, left: 12, zIndex: 2,
                }}
                aria-hidden="true"
              >No Poster Yet</div>
              {/* Giant watermark — editorial typeset, anchored bottom-right.
                  2026-06-04: bumped from 1F (~12% alpha) to 4D (~30% alpha)
                  and shifted hue lift via outer ink-stop so the watermark
                  reads as deliberate flyer typography. Stronger letter
                  spacing crunch + slight rotation gives the screen-printed
                  feel without leaning on a gradient. */}
              <div style={{
                position: 'absolute', bottom: 32, right: 10, left: 10,
                fontSize: 'clamp(48px, 11vw, 72px)', fontWeight: 900, lineHeight: 0.82,
                letterSpacing: '-0.06em',
                color: `${accent}4D`,
                fontFamily: 'var(--font-display)',
                pointerEvents: 'none', userSelect: 'none',
                textTransform: 'uppercase',
                textAlign: 'right',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}>{event.category}</div>
              {/* Small "FLYER DROP SOON" hint above the title — reframes the
                  absence as a promise (Mav: empty states should foreshadow,
                  not apologize). */}
              <div style={{
                position: 'absolute', top: 10, right: 12, zIndex: 2,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                fontSize: 9, fontWeight: 700,
                letterSpacing: '0.16em',
                color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                textTransform: 'uppercase',
              }} aria-hidden="true">FLYER ↓ SOON</div>
              {/* Title */}
              <div style={{
                position: 'relative', zIndex: 2,
              }}>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800,
                  color: darkMode ? '#fff' : '#1a1a1a', lineHeight: 1.15, letterSpacing: '-0.02em',
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>{event.title}</div>
                <div style={{
                  marginTop: 6, fontFamily: 'var(--font-body)', fontSize: 11,
                  color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.7)', fontWeight: 500,
                }}>
                  {(() => {
                    const _slug = findVenueSlug(event.venue);
                    return _slug ? (
                      <a
                        href={`/venue/${_slug}`}
                        className="venue-link"
                        aria-label={`See all upcoming shows at ${event.venue}`}
                        style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px dotted currentColor' }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >{event.venue}</a>
                    ) : event.venue;
                  })()}
                  {' · '}{event.time}
                </div>
              </div>
              {/* Accent bar — solid, brutalist (no gradient tell) */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
                background: accent,
              }} />
            </div>
          );
        })()}

        {/* PUT-ON badge — user-submitted provenance marker */}
        {event.provenance === 'user' && (
          <div style={{
            position: 'absolute', top: 10, left: 10, zIndex: 3,
          }}>
            <PutOnBadge event={event} darkMode={darkMode} />
          </div>
        )}

        {/* Date badge — liquid glass */}
        <div style={{
          position: 'absolute',
          top: event.provenance === 'user' ? 36 : 12,
          left: 12, zIndex: 2,
          ...glassStyle(true, 'btn'),
          background: 'linear-gradient(135deg, rgba(0,0,0,0.68), rgba(0,0,0,0.58))',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: Math.max(radius - 4, 8),
          padding: '9px 13px', display: 'flex', flexDirection: 'column', alignItems: 'center',
          minWidth: 48,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 4px 12px rgba(0,0,0,0.3)',
        }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800,
            color: '#fff', lineHeight: 1,
          }}>{event.date.split(' ')[1]}</span>
          <span style={{
            fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: '#fff', marginTop: 3,
          }}>{event.date.split(' ')[0]}</span>
        </div>

        {/* Category badge — clickable when the parent provides a handler.
            Tapping pivots the grid to "show me only this category" by
            calling setActiveCat upstream. Card's outer link still works
            because we stopPropagation on the chip itself. */}
        {typeof onCategoryClick === 'function' ? (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCategoryClick(event.category); }}
            aria-label={`Filter grid to ${event.category} events`}
            className="focus-double-ring"
            style={{
              position: 'absolute', top: 12, right: 12, zIndex: 2,
              background: catColor,
              color: (typeof window !== 'undefined' && window.categoryTextColor)
                ? window.categoryTextColor(catColor) : '#10110F',
              fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
              letterSpacing: '0.1em', padding: '5px 10px',
              borderRadius: 100,
              border: 'none', cursor: 'pointer',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.06)'; e.currentTarget.style.boxShadow = `0 2px 8px ${catColor}55`; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
          >{event.category}</button>
        ) : (
          // Non-clickable branch — kept catColor so the chip color always
          // means CATEGORY across every surface. Previously fell back to
          // tagColor (city color), which made every Oakland card render an
          // amber NIGHTLIFE chip and every SF card render an orange CARDS
          // chip — the color said "where" while the text said "what."
          <div style={{
            position: 'absolute', top: 12, right: 12, zIndex: 2,
            background: catColor,
            color: (typeof window !== 'undefined' && window.categoryTextColor)
              ? window.categoryTextColor(catColor) : '#10110F',
            fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em', padding: '5px 10px',
            borderRadius: 100,
          }}>{event.category}</div>
        )}

        {/* Date-proximity badge — loss-framing gate v1
            (docs/design/2026-07-16-loss-framing-gate.md). Copy comes ONLY
            from the lib/loss-copy.js registry ('tonight-badge' /
            'tomorrow-badge' keys) — never hardcoded here;
            test/loss-copy-lint.test.js lints the lexicon repo-wide.
            lossCopy() fails closed to null (no badge, neutral card) when:
            the kill switch is off, the event's date signal is missing or
            unparseable, the date isn't the current or next calendar day in
            America/Los_Angeles, or the events.json build stamp
            (module-scope _eventsGenerated, set by loadLiveEvents) is
            absent or older than the entry's 24h freshness ceiling.
            Absolutely positioned below the category chip — occupies no
            layout space, so card geometry is byte-identical with and
            without it. Gate §2.10: max ONE loss-framed element per card;
            the || below is short-circuit (the two predicates are mutually
            exclusive by construction) so at most one key ever renders.
            Fact-badge, not an alarm: static, no motion, no pulse;
            pointer-events off so card tap/keyboard behavior is untouched. */}
        {(() => {
          if (typeof window === 'undefined' || typeof window.lossCopy !== 'function') return null;
          let proximityKey = null;
          let proximityText = null;
          for (const key of ['tonight-badge', 'tomorrow-badge']) {
            proximityText = window.lossCopy(key, event, _eventsGenerated);
            if (proximityText) { proximityKey = key; break; }
          }
          if (!proximityText) return null;
          return (
            <div
              data-loss-copy={proximityKey}
              style={{
                // Temporal-glass grammar (Zora ruling, #101 condition 4):
                // this pill is a TEMPORAL FACT, not taxonomy, so it wears
                // the date badge's dark-glass family — never a category
                // color. The old international-orange body was hex-
                // identical to the MUSIC chip (and a perceptual sibling of
                // FOOD's), stacking two indistinguishable pills on the
                // exact cards where the badge matters most. Card emphasis
                // systems, one job each: category color = taxonomy · dark
                // glass = temporal fact · amber tint = personalization ·
                // depth/ink-frame = hero. Media-area overlays are theme-
                // invariant on purpose (they composite against flyer art,
                // same contract as the date badge). Scrim floor 0.58 keeps
                // 9px white type at 5.32:1 over a pure-white flyer region —
                // the same floor the date badge wears for its 10px month.
                ...glassStyle(true, 'btn'),
                position: 'absolute', top: 42, right: 12, zIndex: 2,
                background: 'linear-gradient(135deg, rgba(0,0,0,0.68), rgba(0,0,0,0.58))',
                border: '1px solid rgba(255,255,255,0.18)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 2px 8px rgba(0,0,0,0.3)',
                color: '#FFFFFF',
                fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
                letterSpacing: '0.1em', padding: '5px 10px',
                borderRadius: 100,
                pointerEvents: 'none',
              }}
            >{proximityText}</div>
          );
        })()}

        {/* For You badge */}
        {forYou && (
          <div style={{
            position: 'absolute', bottom: 12, right: 12, zIndex: 2,
            background: 'rgba(213,120,0,0.9)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            color: '#10110F',
            fontFamily: 'var(--font-body)', fontSize: 8, fontWeight: 700,
            letterSpacing: '0.12em', padding: '4px 10px',
            borderRadius: 100,
            boxShadow: '0 2px 8px rgba(213,120,0,0.3)',
          }}>YOUR WAVE</div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '12px 14px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* City chip — ABSENT, not blank, when the authority could not place
            the venue. No placeholder, no "Bay Area", no reserved empty band:
            the card simply starts at its title, and the reader is told nothing
            instead of told wrong. */}
        {event.tag && (
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: tagColor, marginBottom: 5,
          }}>{event.tag}</div>
        )}

        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700,
          color: darkMode ? 'var(--on-surface)' : '#1a1a1a',
          lineHeight: 1.25, marginBottom: 8,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>{event.title}</div>

        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '18px',
          color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
          flex: 1, marginBottom: 10,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>{event.desc}</div>

        {/* Footer. A card with an occasion kicker wraps (Zora spec
            2026-10-01 §2, Vale's footer rule): at 375px the chip row
            already squeezes time + venue to 44px, and an ~84px kicker would
            take that column to zero. So the chip row drops beneath time and
            venue and runs right-aligned on its own line, kicker first, at
            every width. Untagged cards keep the exact pre-lane footer. */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderTop: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)',
          paddingTop: 8,
          ...(occasionPill ? { flexWrap: 'wrap', rowGap: 8, columnGap: 12 } : null),
        }}>
          <div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: darkMode ? '#fff' : '#333' }}>{event.time}</div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: darkMode ? '#fff' : '#333', display: 'inline-flex', alignItems: 'center' }}>
              {(() => {
                const _slug = findVenueSlug(event.venue);
                return _slug ? (
                  <a
                    href={`/venue/${_slug}`}
                    className="venue-link"
                    aria-label={`See all upcoming shows at ${event.venue}`}
                    style={{
                      color: 'inherit',
                      textDecoration: 'underline dotted',
                      textDecorationThickness: '1px',
                      textUnderlineOffset: '3px',
                      textDecorationColor: darkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {event.venue}
                  </a>
                ) : (
                  <span>{event.venue}</span>
                );
              })()}
              {VenueHeatPill && (
                <VenueHeatPill placeId={event.place_id} heat={heat || {}} />
              )}
            </div>
          </div>
          <div style={occasionPill
            ? { display: 'flex', alignItems: 'center', columnGap: 8, rowGap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto' }
            : { display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* OCCASION kicker: the row's season (events.json `occasion`),
                first in the chip row, immediately LEFT of ENTRY; DOM order
                is the keyboard order (kicker → ENTRY → MERCH → share). It
                wears the temporal-glass family of the same-day badge,
                because an occasion is a WHEN fact, not taxonomy, so it
                never takes a category color and the card keeps exactly one
                category-accented pill. Links to the occasion hub. Renders
                only for a rostered slug; markup, tokens and the
                stopPropagation guards live in lib/occasion-kicker.js. */}
            {occasionPill && <a {...occasionPill.props}>{occasionPill.label}</a>}
            {/* ENTRY chip — venue rules + bag policy, immediately LEFT of
                MERCH (info-before-commerce; the two morphing chips sit
                adjacent). Fog-blue civic signage: leading 5×5 square tell
                (amber in drift), NO trailing arrow — square-before vs
                arrow-after is the sibling distinction from MERCH. Renders
                ONLY when lib/venue-policies-gate.js says the venue's policy
                is curated, sourced and inside both freshness clocks; no
                entry / suppressed → chip ABSENT (cards without policy are
                byte-identical to before this feature). Also renders for a
                row whose own cited rules (events.json `rules`) pass the
                same gate — a park has no venue page. Same stopPropagation
                + onKeyDown guards as MERCH (B2 Enter-bubbling). Fallback
                when RulesPopover hasn't loaded: open the venue's own policy
                page — the venue's page IS the truth (spec §1). */}
            {rulesPolicy && (
              <button
                type="button"
                className="focus-double-ring"
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof window.RulesPopover === 'function') {
                    setRulesTriggerRect(e.currentTarget.getBoundingClientRect());
                  } else {
                    window.open(rulesPolicy.entry.source_url, '_blank', 'noopener,noreferrer');
                  }
                }}
                onKeyDown={chipKeyDownGuard}
                aria-label={rulesChipAria(rulesChipLabel(rulesPolicy, event), rulesPolicy.entry.source_kind)}
                aria-haspopup="dialog"
                title={rulesChipTitle(rulesChipLabel(rulesPolicy, event), rulesPolicy.entry.source_kind)}
                style={rulesChipStyle(darkMode)}
              >
                {!rulesChipSquareHidden() && (
                  <span aria-hidden="true" style={rulesChipSquareStyle(darkMode, rulesPolicy.state)} />
                )}
                ENTRY
              </button>
            )}
            {/* MERCH chip — outbound link to the artist's official store.
                Renders only when scrapers/attach-merch.js attached event.merch
                (root-domain allowlisted, $15 price floor, not cancelled).
                Amber per Zora spec — orange is reserved for TICKETS primary.
                rel="sponsored nofollow" per Vox; onClick stopPropagation so
                card-tap stays on the slide ritual, and onKeyDown stopPropagation
                (B2) stops Enter from bubbling to the article's onKeyDown, which
                would preventDefault + fire window.open (the ticket link) on top
                of the merch action — same guard the transit strip carries.
                Disclosure label "OFFICIAL STORE" is non-negotiable per Atlas
                (FTC implied-endorsement gate). aria-haspopup="dialog" +
                .focus-double-ring: 2026-08-20 parity retrofit with the ENTRY
                chip beside it — the debt the rules spec §1 logged. */}
            {event.merch && typeof event.merch.shop_url === 'string' && event.slug && (
              <button
                type="button"
                className="focus-double-ring"
                onClick={(e) => {
                  e.stopPropagation();
                  // Capture the chip's rect for the FLIP morph and let
                  // MerchPopover mount + morph out of the chip's position.
                  // If MerchPopover hasn't loaded yet (race on first paint
                  // or no-JS), fall back to the standalone popup window —
                  // /merch-preview.html stays alive as the URL fallback +
                  // crawler target.
                  if (typeof window.MerchPopover === 'function') {
                    setMerchTriggerRect(e.currentTarget.getBoundingClientRect());
                  } else {
                    const w = 760, h = 760;
                    const left = Math.max(0, (window.screen.availWidth - w) / 2);
                    const top = Math.max(0, (window.screen.availHeight - h) / 2);
                    const url = `/merch-preview.html?slug=${encodeURIComponent(event.slug)}`;
                    window.open(url, `OnSumnMerch_${event.slug}`,
                      `popup,width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`);
                  }
                }}
                onKeyDown={chipKeyDownGuard}
                aria-label={`Preview ${event.merch.artist || 'artist'} merch`}
                aria-haspopup="dialog"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: 'var(--amber-gold-ink)',
                  background: darkMode ? 'rgba(213,120,0,0.12)' : 'rgba(213,120,0,0.18)',
                  border: '1px solid rgba(213,120,0,0.55)',
                  padding: '3px 8px', borderRadius: 100,
                  lineHeight: 1.2, minHeight: 24, cursor: 'pointer',
                }}
                title="Click to view merch"
              >MERCH <span aria-hidden="true">→</span></button>
            )}
            {/* SlidingChip — count display only. Tap-to-slide stays on the card body.
                Threshold rules: <3 nothing, 3-9 "X sliding", ≥10 "🔥 X sliding". */}
            {event.slug && typeof SlidingChip === 'function' && (
              <SlidingChip slug={event.slug} darkMode={darkMode} />
            )}
            {/* Stroked square+arrow share icon, 44x44 hit area, stopPropagation so
                tap doesn't fire card's tap-to-slide. */}
            {event.slug && typeof ShareIconButton === 'function' && (
              <ShareIconButton event={event} darkMode={darkMode} />
            )}
            {event.provenance === 'user' && event.slug && (
              <ReportButton slug={event.slug} darkMode={darkMode} />
            )}
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.05em', textTransform: 'uppercase',
              color: hov ? 'var(--international-orange-ink)' : (darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)'),
              transition: 'color 0.2s',
            }}>I'M SLIDING →</div>
          </div>
        </div>
        {/* Transit strip — full-bleed bottom utility bar (addendum §1.1). A
            round-trip lens deep-link, APPENDED below the footer so no existing
            card element moves (card uniformity is a standing sensitivity).
            Gate: slug + numeric lat/lng (window.transitCtaVisible) — coord-less
            rows render nothing (no placeholder), so cards stay uniform. onClick
            stopPropagation keeps the card body's slide ritual off this tap (the
            venue-link precedent above); onKeyDown stopPropagation stops Enter
            from bubbling to the article's onKeyDown, which would preventDefault
            this anchor's navigation and fire window.open instead. */}
        {(typeof window !== 'undefined' && typeof window.transitCtaVisible === 'function'
          ? window.transitCtaVisible(event)
          : !!(event.slug && Number.isFinite(event.lat) && Number.isFinite(event.lng))) && (
          <a
            href={typeof window !== 'undefined' && window.transitCtaHref
              ? window.transitCtaHref(event.slug)
              : `/flows?event=${encodeURIComponent(event.slug)}`}
            className="focus-double-ring"
            aria-label={typeof window !== 'undefined' && window.transitCtaAria
              ? window.transitCtaAria(event.title)
              : `Can I get there + back? Clock the ride to ${event.title}`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onMouseEnter={(e) => { e.currentTarget.style.background = darkMode ? 'rgba(213,120,0,0.08)' : 'rgba(213,120,0,0.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            onFocus={(e) => { e.currentTarget.style.background = darkMode ? 'rgba(213,120,0,0.08)' : 'rgba(213,120,0,0.10)'; }}
            onBlur={(e) => { e.currentTarget.style.background = 'transparent'; }}
            style={{
              margin: '10px -14px -14px',
              padding: '0 14px',
              minHeight: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderTop: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)',
              background: 'transparent',
              textDecoration: 'none',
              color: darkMode ? '#FFB778' : '#8E4E00',
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              transition: 'background 0.2s ease',
            }}
          >
            <span>{(typeof window !== 'undefined' && window.TRANSIT_CTA_LABEL) || 'CAN I GET THERE + BACK?'}</span>
            <span aria-hidden="true" style={{ fontSize: 14 }}>⇄</span>
          </a>
        )}
      </div>
      {merchTriggerRect && typeof window.MerchPopover === 'function' && (
        <window.MerchPopover
          event={event}
          darkMode={darkMode}
          triggerRect={merchTriggerRect}
          onClose={() => setMerchTriggerRect(null)}
        />
      )}
      {rulesTriggerRect && rulesPolicy && typeof window.RulesPopover === 'function' && (
        <window.RulesPopover
          event={event}
          entry={rulesPolicy.entry}
          state={rulesPolicy.state}
          eventRules={rulesPolicy.eventRules}
          darkMode={darkMode}
          triggerRect={rulesTriggerRect}
          onClose={() => setRulesTriggerRect(null)}
        />
      )}
    </article>
  );
}

EventCard.propTypes = {
  event: PropTypes.object.isRequired,
  darkMode: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  radius: PropTypes.number.isRequired,
  taste: PropTypes.object,
  venueHeat: PropTypes.object,
  emphasis: PropTypes.oneOf(['hero', 'hero-live', 'quiet']),
};

window.safeHref = safeHref;
window.isHeroCard = isHeroCard;
window.EventCard = EventCard;
window.ALL_EVENTS = ALL_EVENTS;
window.useEvents = useEvents;
window.loadLiveEvents = loadLiveEvents;
window.loadUserEvents = loadUserEvents;
window.addEvent = addEvent;
window.scrollToEvent = scrollToEvent;
// window.TAG_COLORS is gone with the table it published. Map.jsx was its only
// consumer and now imports cityInk from lib/city-registry.js directly — one
// authority reached the same way from every surface, with no load-order race
// between two script tags deciding whether a city gets a colour at all.
// fetchInterestCounts now exported by Interest.jsx.
window.useVenueHeat = useVenueHeat;
window.loadVenueHeat = loadVenueHeat;
window.dedupEvents = dedupEvents;
window.normalizeWorkerEvent = normalizeWorkerEvent;
